import React, { useEffect, useState } from 'react';
import { Clock, CalendarClock, Server, CalendarCheck2, Type, Radar, Lightbulb, Tag, Bell, Battery, Smartphone, HardDrive } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getFtpCaptureConfig,
  updateFtpCaptureConfig,
  testFtpConnectivity,
  getFtpSchedule,
  updateFtpSchedule,
  getOsdConfig,
  updateOsdConfig,
  getRecordingSchedule,
  updateRecordingSchedule,
  getTimezone,
  updateTimezone,
  getPirConfig,
  updatePirConfig,
  getLedState,
  updateLedState,
  getGeneralConfig,
  updateGeneralConfig,
  getAlarmConfig,
  updateAlarmConfig,
  getBatteryStatus,
  getMobileNetwork,
  getSdCard,
  formatSdCard,
} from '../api/ropApi';
import WeeklyScheduleGrid from './WeeklyScheduleGrid';
import { useIsAdmin } from '../hooks/useIsAdmin';

const GENERIC_ERROR_MESSAGE = 'No se pudo completar la operación.';

const getErrorMessage = (error) => {
  const message = error?.response?.data?.message;
  // Only ever return a string: a non-string message (object/array from a
  // malformed or validation-style payload) would otherwise reach a React
  // child (MobileNetworkCard's inline error) and crash the render — there is
  // no ErrorBoundary in this app to contain that.
  return typeof message === 'string' && message ? message : GENERIC_ERROR_MESSAGE;
};

// The ROP UI (phases 1-2) never introduced a channel selector, so every
// per-channel settings endpoint used here (ftp-schedule, osd,
// recording-schedule) defaults to channel 0 - the same default channel used
// elsewhere in this feature (getSnapshot, moveRotate).
const CHANNEL_ID = 0;

// TimezoneRequest.offset is @Min(-43200) @Max(50400) seconds. Displaying raw
// seconds to a human is unfriendly, so this form shows/edits HOURS instead
// (step 0.5h == 1800s, always an exact integer number of seconds) and
// converts to/from seconds only at the API boundary. Deliberate UX choice,
// not part of the backend contract.
const TIMEZONE_MIN_SECONDS = -43200;
const TIMEZONE_MAX_SECONDS = 50400;
const TIMEZONE_MIN_HOURS = TIMEZONE_MIN_SECONDS / 3600;
const TIMEZONE_MAX_HOURS = TIMEZONE_MAX_SECONDS / 3600;

const saveButtonClasses = (disabled) =>
  `rounded-lg bg-blue-600 text-white py-2 px-4 text-sm font-medium hover:bg-blue-700 transition-colors disabled:hover:bg-blue-600 ${
    disabled ? 'opacity-60 cursor-not-allowed' : ''
  }`;

const inputClasses = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm';
const labelClasses = 'block text-sm text-gray-600 mb-1';

const CardShell = ({ icon: Icon, title, testId, children }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-4">
      <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
        <Icon size={18} />
      </div>
      <h3 className="text-base font-semibold text-gray-800">{title}</h3>
    </div>
    {children}
  </div>
);

// Shown whenever a GET fails — ROP devices (4G ones especially) can be
// briefly unreachable, so this is a recoverable state, not a dead end: the
// user just clicks to re-fire that one card's request.
const ErrorRetry = ({
  message = 'No se pudo cargar la información. La cámara puede estar desconectada.',
  onRetry,
}) => (
  <div>
    <p className="text-sm text-gray-500 mb-3">{message}</p>
    <button
      type="button"
      onClick={onRetry}
      className="rounded-lg border border-gray-200 text-gray-700 py-2 px-4 text-sm font-medium hover:bg-gray-50 transition-colors"
    >
      Reintentar
    </button>
  </div>
);

// ---- Enumerated fields (shared) ----

// Every option list below comes from the "Value Range" column of the ROP API
// V2.1.3 PUT (set) sections. The option value is the raw ROP enum string, which
// is exactly what gets submitted; only the label is human-readable Spanish.
const FTP_STREAM_TYPE_OPTIONS = [
  { value: 'MAIN_WITH_IMAGE', label: 'Video de stream principal + imagen' },
  { value: 'SUB_WITH_IMAGE', label: 'Video de sub-stream + imagen' },
  { value: 'EXTERN_WITH_IMAGE', label: 'Video de stream extendido + imagen' },
  { value: 'IMAGE_ONLY', label: 'Solo imagen' },
  { value: 'MAIN', label: 'Video de stream principal' },
  { value: 'SUB', label: 'Video de sub-stream' },
  { value: 'EXTERN', label: 'Video de stream extendido' },
];
const FTP_MODE_OPTIONS = [
  { value: 'AUTO', label: 'Automático' },
  { value: 'PORT', label: 'Activo' },
  { value: 'PASV', label: 'Pasivo' },
];
const FTP_OVERWRITE_POLICY_OPTIONS = [
  { value: 'NONE', label: 'No sobrescribir (cada archivo usa su nombre con fecha y hora)' },
  { value: 'SINGLE', label: 'Sobrescribir solo si coincide la marca de tiempo' },
  { value: 'PING_PONG', label: 'Sobrescritura ping-pong' },
];
const FTP_IMAGE_CAPTURE_TYPE_OPTIONS = [
  { value: 'MAIN_STREAM', label: 'Alta definición' },
  { value: 'EXTERN_STREAM', label: 'Media definición' },
  { value: 'SUB_STREAM', label: 'Baja definición' },
];
const FTP_DIRECTORY_NAMING_OPTIONS = [
  { value: 'DATE', label: 'Crear carpeta por fecha' },
  { value: 'CUSTOM', label: 'Nombre de carpeta personalizado' },
];
const LED_IR_STATE_OPTIONS = [
  { value: 'AUTO', label: 'Automático' },
  { value: 'MANUAL_ON', label: 'Encendido manual' },
  { value: 'MANUAL_OFF', label: 'Apagado manual' },
];
const LED_POWER_STATE_OPTIONS = [
  { value: 'ON', label: 'Encendido' },
  { value: 'OFF', label: 'Apagado' },
];
const LED_DOORBELL_STATE_OPTIONS = [
  { value: 'CLOSE', label: 'Apagado' },
  { value: 'KEEP_OFF', label: 'Mantener apagado' },
  { value: 'KEEP_ON', label: 'Mantener encendido' },
  { value: 'OPEN', label: 'Encendido' },
];

// A value returned by GET that is not in the documented list (older firmware,
// doc drift) must not be silently dropped: it is appended as an extra option so
// the select keeps showing it and a save round-trips it untouched.
const withCurrentValue = (options, current) => {
  const currentText = current === null || current === undefined ? '' : String(current);
  if (currentText === '' || options.some((option) => String(option.value) === currentText)) {
    return options;
  }
  return [...options, { value: currentText, label: `${currentText} (valor actual)` }];
};

const EnumSelect = ({ id, label, value, options, disabled, onChange }) => {
  const allOptions = withCurrentValue(options, value);
  return (
    <div>
      <label htmlFor={id} className={labelClasses}>{label}</label>
      <select
        id={id}
        disabled={disabled}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={inputClasses}
      >
        {(value === '' || value === null || value === undefined) && (
          <option value="">Seleccioná una opción</option>
        )}
        {allOptions.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  );
};

// ---- Zona horaria ----

const TimezoneCard = ({ uid }) => {
  const [hours, setHours] = useState('');
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    getTimezone(uid)
      .then((data) => {
        if (cancelled) return;
        setHours(String((data?.offset ?? 0) / 3600));
      })
      .catch((error) => {
        if (cancelled) return;
        toast.error(getErrorMessage(error));
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, retryToken]);

  const hoursValue = hours === '' ? NaN : Number(hours);
  const isInvalid =
    hours === '' ||
    Number.isNaN(hoursValue) ||
    hoursValue < TIMEZONE_MIN_HOURS ||
    hoursValue > TIMEZONE_MAX_HOURS;

  const handleSave = async () => {
    if (isInvalid) return;
    setSaving(true);
    try {
      const offset = Math.round(hoursValue * 3600);
      await updateTimezone(uid, { offset });
      toast.success('Zona horaria actualizada.');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <CardShell icon={Clock} title="Zona horaria" testId="card-timezone">
      {failed ? (
        <ErrorRetry onRetry={() => setRetryToken((token) => token + 1)} />
      ) : (
        <>
          <label htmlFor="rop-timezone-hours" className={labelClasses}>
            Zona horaria (horas respecto a UTC)
          </label>
          <input
            id="rop-timezone-hours"
            type="number"
            step="0.5"
            min={TIMEZONE_MIN_HOURS}
            max={TIMEZONE_MAX_HOURS}
            disabled={loading}
            value={hours}
            onChange={(event) => setHours(event.target.value)}
            className={`${inputClasses} mb-2`}
          />
          {isInvalid && (
            <p className="text-xs text-red-600 mb-2">
              Ingresá un valor entre {TIMEZONE_MIN_HOURS} y {TIMEZONE_MAX_HOURS} horas.
            </p>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading || isInvalid}
            className={saveButtonClasses(saving || loading || isInvalid)}
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      )}
    </CardShell>
  );
};

// ---- Horario de grabación / FTP — Horario (shared shape) ----

// El backend devuelve el `type` de cada entrada del calendario en MAYÚSCULA
// (ver TypeSchedule.java y la sección recording-schedule / ftp-schedule del
// doc de la API ROP V2.1.3: PEOPLE, VEHICLE, ANIMAL, PACKAGE, OTHER, NORMAL).
// Este diccionario solo traduce la etiqueta mostrada; el valor crudo sigue
// viajando sin cambios en el PUT.
const SCHEDULE_TYPE_LABELS = {
  NORMAL: 'Timelapse',
  PEOPLE: 'Personas',
  VEHICLE: 'Vehiculos',
  OTHER: 'Otros',
};

// Fallback para valores no mapeados (p. ej. ANIMAL, PACKAGE): se capitaliza
// el valor original en vez de mostrar undefined o romper el render.
const scheduleTypeLabel = (type) => {
  if (SCHEDULE_TYPE_LABELS[type]) return SCHEDULE_TYPE_LABELS[type];
  if (typeof type !== 'string' || type === '') return type;
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};

const ScheduleCard = ({ uid, testId, icon, title, getFn, updateFn, successMessage }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState(false);
  const [typeScheduleList, setTypeScheduleList] = useState([]);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    getFn(uid, CHANNEL_ID)
      .then((data) => {
        if (cancelled) return;
        setState(Boolean(data?.state));
        setTypeScheduleList(data?.typeScheduleList || []);
      })
      .catch((error) => {
        if (cancelled) return;
        toast.error(getErrorMessage(error));
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, getFn, retryToken]);

  const handleGridChange = (index, newValue) => {
    setTypeScheduleList((prev) =>
      prev.map((item, i) => (i === index ? { ...item, value: newValue } : item))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateFn(uid, { channelId: CHANNEL_ID, state, typeScheduleList });
      toast.success(successMessage);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (failed) {
    return (
      <CardShell icon={icon} title={title} testId={testId}>
        <ErrorRetry onRetry={() => setRetryToken((token) => token + 1)} />
      </CardShell>
    );
  }

  return (
    <CardShell icon={icon} title={title} testId={testId}>
      <label className="flex items-center gap-2 mb-4 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={state}
          disabled={loading}
          onChange={(event) => setState(event.target.checked)}
          className="rounded border-gray-300"
        />
        Activado
      </label>

      {loading ? (
        <p className="text-sm text-gray-500">Cargando…</p>
      ) : typeScheduleList.length === 0 ? (
        <p className="text-sm text-gray-500 mb-4">Sin horarios configurados.</p>
      ) : (
        <div className="space-y-4 mb-4">
          {typeScheduleList.map((item, index) => (
            <div key={`${item.type}-${index}`}>
              <p className="text-xs font-semibold text-gray-600 mb-1">{scheduleTypeLabel(item.type)}</p>
              <WeeklyScheduleGrid
                value={item.value}
                onChange={(newValue) => handleGridChange(index, newValue)}
                disabled={saving}
              />
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={handleSave}
        disabled={saving || loading}
        className={saveButtonClasses(saving || loading)}
      >
        {saving ? 'Guardando…' : 'Guardar'}
      </button>
    </CardShell>
  );
};

// ---- FTP — Configuración ----

// Required/optional split mirrors FtpCaptureRequest.java exactly:
// @NotBlank host/ftpUsername/ftpPassword/streamType/ftpMode,
// @NotNull port/ftpsState are required; everything else (incl. remoteFilePath) is optional.
// videoIntervalsOptions/extraFeatures are GET-only capability lists (no
// setter in FtpCaptureRequest) so they're excluded from the form/PUT body
// and shown as read-only info text instead.
// The Java source has no @Pattern/enum allow-list, but the ROP API doc lists
// the allowed values for streamType, ftpMode, the two overwrite policies,
// imageCaptureType and autoDirectoryNamingRule, so those render as selects
// (see the option tables above). uploadVideoIntervals is a select built from
// the GET's videoIntervalsOptions, with a number-input fallback.
// Switches (ftpsState, anonymousFtpState) travel as booleans; every other
// enum-like field is the raw ROP string, exactly as GET returns it
// (autoDirectoryNamingRule DATE/CUSTOM, image/videoOverwritePolicy
// NONE/SINGLE/PING_PONG, imageCaptureType MAIN_STREAM/EXTERN_STREAM/
// SUB_STREAM). imageCaptureHeight/Width and the *FileNameForOverwrite fields
// have no input but are read and sent back untouched so the PUT round-trip
// does not drop them.
const FTP_CAPTURE_DEFAULTS = {
  host: '',
  port: '',
  remoteFilePath: '',
  ftpUsername: '',
  ftpPassword: '',
  streamType: '',
  ftpMode: '',
  ftpsState: false,
  anonymousFtpState: false,
  uploadVideoIntervals: '',
  autoDirectoryNamingRule: '',
  imageOverwritePolicy: '',
  videoOverwritePolicy: '',
  imageCaptureType: '',
  imageCaptureHeight: '',
  imageCaptureWidth: '',
  imageFileNameForOverwrite: '',
  videoFileNameForOverwrite: '',
  videoFileSize: '',
  uploadImageIntervals: '',
};

const FtpCaptureCard = ({ uid }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState(FTP_CAPTURE_DEFAULTS);
  const [readOnlyInfo, setReadOnlyInfo] = useState({ videoIntervalsOptions: [], extraFeatures: [] });
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    getFtpCaptureConfig(uid)
      .then((data) => {
        if (cancelled) return;
        setForm({
          host: data?.host ?? '',
          port: data?.port ?? '',
          remoteFilePath: data?.remoteFilePath ?? '',
          ftpUsername: data?.ftpUsername ?? '',
          ftpPassword: data?.ftpPassword ?? '',
          streamType: data?.streamType ?? '',
          // Some cameras omit ftpMode in GET; the field is required on PUT, so
          // default to the doc's automatic mode instead of blocking the save.
          ftpMode: data?.ftpMode || 'AUTO',
          ftpsState: Boolean(data?.ftpsState),
          anonymousFtpState: Boolean(data?.anonymousFtpState),
          uploadVideoIntervals: data?.uploadVideoIntervals ?? '',
          autoDirectoryNamingRule: data?.autoDirectoryNamingRule ?? '',
          imageOverwritePolicy: data?.imageOverwritePolicy ?? '',
          videoOverwritePolicy: data?.videoOverwritePolicy ?? '',
          imageCaptureType: data?.imageCaptureType ?? '',
          imageCaptureHeight: data?.imageCaptureHeight ?? '',
          imageCaptureWidth: data?.imageCaptureWidth ?? '',
          imageFileNameForOverwrite: data?.imageFileNameForOverwrite ?? '',
          videoFileNameForOverwrite: data?.videoFileNameForOverwrite ?? '',
          videoFileSize: data?.videoFileSize ?? '',
          uploadImageIntervals: data?.uploadImageIntervals ?? '',
        });
        setReadOnlyInfo({
          videoIntervalsOptions: data?.videoIntervalsOptions || [],
          extraFeatures: data?.extraFeatures || [],
        });
      })
      .catch((error) => {
        if (cancelled) return;
        toast.error(getErrorMessage(error));
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, retryToken]);

  const handleField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelect = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Video interval choices come from the GET's videoIntervalsOptions (the
  // backend DTO also accepts the spec-example alias intervalsOptions).
  const videoIntervalChoices = readOnlyInfo.videoIntervalsOptions.map((seconds) => ({
    value: String(seconds),
    label: `${seconds} seg.`,
  }));

  const buildBody = () => ({
    host: form.host,
    port: form.port === '' ? null : Number(form.port),
    remoteFilePath: form.remoteFilePath,
    ftpUsername: form.ftpUsername,
    ftpPassword: form.ftpPassword,
    streamType: form.streamType,
    ftpMode: form.ftpMode,
    ftpsState: form.ftpsState,
    anonymousFtpState: form.anonymousFtpState,
    uploadVideoIntervals: form.uploadVideoIntervals === '' ? null : Number(form.uploadVideoIntervals),
    autoDirectoryNamingRule: form.autoDirectoryNamingRule || null,
    imageOverwritePolicy: form.imageOverwritePolicy || null,
    videoOverwritePolicy: form.videoOverwritePolicy || null,
    imageCaptureType: form.imageCaptureType || null,
    imageCaptureHeight: form.imageCaptureHeight === '' ? null : Number(form.imageCaptureHeight),
    imageCaptureWidth: form.imageCaptureWidth === '' ? null : Number(form.imageCaptureWidth),
    imageFileNameForOverwrite: form.imageFileNameForOverwrite,
    videoFileNameForOverwrite: form.videoFileNameForOverwrite,
    videoFileSize: form.videoFileSize === '' ? null : Number(form.videoFileSize),
    uploadImageIntervals: form.uploadImageIntervals === '' ? null : Number(form.uploadImageIntervals),
  });

  const isValid =
    form.host.trim() !== '' &&
    form.ftpUsername.trim() !== '' &&
    form.ftpPassword.trim() !== '' &&
    form.streamType.trim() !== '' &&
    form.ftpMode.trim() !== '' &&
    form.port !== '' &&
    !Number.isNaN(Number(form.port));

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      await updateFtpCaptureConfig(uid, buildBody());
      toast.success('Configuración FTP actualizada.');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const result = await testFtpConnectivity(uid, buildBody());
      if (result?.code === 0) {
        toast.success(result?.message || 'Conexión exitosa.');
      } else {
        toast.error(result?.message || GENERIC_ERROR_MESSAGE);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setTesting(false);
    }
  };

  const disabled = loading || saving || testing;

  if (failed) {
    return (
      <CardShell icon={Server} title="FTP — Configuración" testId="card-ftp-capture">
        <ErrorRetry onRetry={() => setRetryToken((token) => token + 1)} />
      </CardShell>
    );
  }

  return (
    <CardShell icon={Server} title="FTP — Configuración" testId="card-ftp-capture">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label htmlFor="ftp-host" className={labelClasses}>Host</label>
          <input id="ftp-host" type="text" disabled={disabled} value={form.host} onChange={handleField('host')} className={inputClasses} />
        </div>
        <div>
          <label htmlFor="ftp-port" className={labelClasses}>Puerto</label>
          <input id="ftp-port" type="number" disabled={disabled} value={form.port} onChange={handleField('port')} className={inputClasses} />
        </div>
        <div>
          <label htmlFor="ftp-remoteFilePath" className={labelClasses}>Ruta remota</label>
          <input id="ftp-remoteFilePath" type="text" disabled={disabled} value={form.remoteFilePath} onChange={handleField('remoteFilePath')} className={inputClasses} />
        </div>
        <div>
          <label htmlFor="ftp-username" className={labelClasses}>Usuario FTP</label>
          <input id="ftp-username" type="text" disabled={disabled} value={form.ftpUsername} onChange={handleField('ftpUsername')} className={inputClasses} />
        </div>
        <div>
          <label htmlFor="ftp-password" className={labelClasses}>Contraseña FTP</label>
          <input id="ftp-password" type="password" disabled={disabled} value={form.ftpPassword} onChange={handleField('ftpPassword')} className={inputClasses} />
        </div>
        <EnumSelect
          id="ftp-streamType"
          label="Tipo de stream"
          value={form.streamType}
          options={FTP_STREAM_TYPE_OPTIONS}
          disabled={disabled}
          onChange={handleSelect('streamType')}
        />
        <EnumSelect
          id="ftp-mode"
          label="Modo FTP"
          value={form.ftpMode}
          options={FTP_MODE_OPTIONS}
          disabled={disabled}
          onChange={handleSelect('ftpMode')}
        />
        {videoIntervalChoices.length > 0 ? (
          <EnumSelect
            id="ftp-uploadVideoIntervals"
            label="Intervalo de subida de video (seg.)"
            value={String(form.uploadVideoIntervals)}
            options={videoIntervalChoices}
            disabled={disabled}
            onChange={handleSelect('uploadVideoIntervals')}
          />
        ) : (
          <div>
            <label htmlFor="ftp-uploadVideoIntervals" className={labelClasses}>Intervalo de subida de video (seg.)</label>
            <input id="ftp-uploadVideoIntervals" type="number" disabled={disabled} value={form.uploadVideoIntervals} onChange={handleField('uploadVideoIntervals')} className={inputClasses} />
          </div>
        )}
        <EnumSelect
          id="ftp-imageOverwritePolicy"
          label="Política de sobrescritura de imagen"
          value={form.imageOverwritePolicy}
          options={FTP_OVERWRITE_POLICY_OPTIONS}
          disabled={disabled}
          onChange={handleSelect('imageOverwritePolicy')}
        />
        <EnumSelect
          id="ftp-videoOverwritePolicy"
          label="Política de sobrescritura de video"
          value={form.videoOverwritePolicy}
          options={FTP_OVERWRITE_POLICY_OPTIONS}
          disabled={disabled}
          onChange={handleSelect('videoOverwritePolicy')}
        />
        <EnumSelect
          id="ftp-imageCaptureType"
          label="Calidad de captura de imagen"
          value={form.imageCaptureType}
          options={FTP_IMAGE_CAPTURE_TYPE_OPTIONS}
          disabled={disabled}
          onChange={handleSelect('imageCaptureType')}
        />
        <EnumSelect
          id="ftp-autoDirectoryNamingRule"
          label="Nomenclatura automática de directorios"
          value={form.autoDirectoryNamingRule}
          options={FTP_DIRECTORY_NAMING_OPTIONS}
          disabled={disabled}
          onChange={handleSelect('autoDirectoryNamingRule')}
        />
        <div>
          <label htmlFor="ftp-videoFileSize" className={labelClasses}>Tamaño de archivo de video</label>
          <input id="ftp-videoFileSize" type="number" disabled={disabled} value={form.videoFileSize} onChange={handleField('videoFileSize')} className={inputClasses} />
        </div>
        <div>
          <label htmlFor="ftp-uploadImageIntervals" className={labelClasses}>Intervalo de subida de imagen (seg.)</label>
          <input id="ftp-uploadImageIntervals" type="number" disabled={disabled} value={form.uploadImageIntervals} onChange={handleField('uploadImageIntervals')} className={inputClasses} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-3">
        <label htmlFor="ftp-ftpsState" className="flex items-center gap-2 text-sm text-gray-700">
          <input id="ftp-ftpsState" type="checkbox" disabled={disabled} checked={form.ftpsState} onChange={handleField('ftpsState')} className="rounded border-gray-300" />
          Usar FTPS
        </label>
        <label htmlFor="ftp-anonymousFtpState" className="flex items-center gap-2 text-sm text-gray-700">
          <input id="ftp-anonymousFtpState" type="checkbox" disabled={disabled} checked={form.anonymousFtpState} onChange={handleField('anonymousFtpState')} className="rounded border-gray-300" />
          FTP anónimo
        </label>
      </div>

      {(readOnlyInfo.videoIntervalsOptions.length > 0 || readOnlyInfo.extraFeatures.length > 0) && (
        <div className="text-xs text-gray-500 mb-3 space-y-0.5">
          {readOnlyInfo.videoIntervalsOptions.length > 0 && (
            <p>Intervalos soportados: {readOnlyInfo.videoIntervalsOptions.join(', ')}</p>
          )}
          {readOnlyInfo.extraFeatures.length > 0 && (
            <p>Funciones adicionales: {readOnlyInfo.extraFeatures.join(', ')}</p>
          )}
        </div>
      )}

      {!isValid && !loading && (
        <p className="text-xs text-red-600 mb-2">Completá los campos obligatorios antes de guardar.</p>
      )}

      <div className="flex items-center gap-2">
        <button type="button" onClick={handleSave} disabled={disabled || !isValid} className={saveButtonClasses(disabled || !isValid)}>
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
        <button
          type="button"
          onClick={handleTestConnection}
          disabled={disabled}
          className={`rounded-lg border border-gray-200 py-2 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors ${
            disabled ? 'opacity-60 cursor-not-allowed' : ''
          }`}
        >
          {testing ? 'Probando…' : 'Probar conexión'}
        </button>
      </div>
    </CardShell>
  );
};

// ---- OSD ----

// ROP position enums, passed through as-is: horizontal LEFT/MID/RIGHT,
// vertical UP/DOWN. Switches (state, watermarkState) stay booleans.
const OSD_HORIZONTAL_OPTIONS = [
  { value: 'LEFT', label: 'Izquierda' },
  { value: 'MID', label: 'Centro' },
  { value: 'RIGHT', label: 'Derecha' },
];
const OSD_VERTICAL_OPTIONS = [
  { value: 'UP', label: 'Arriba' },
  { value: 'DOWN', label: 'Abajo' },
];

const OSD_DATETIME_DEFAULTS = { state: false, horizontalPosition: 'LEFT', verticalPosition: 'UP' };
const OSD_CHANNELNAME_DEFAULTS = {
  name: '',
  state: false,
  horizontalPosition: 'LEFT',
  verticalPosition: 'DOWN',
  watermarkState: false,
};

const OsdCard = ({ uid }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [osdDateTime, setOsdDateTime] = useState(OSD_DATETIME_DEFAULTS);
  const [osdChannelName, setOsdChannelName] = useState(OSD_CHANNELNAME_DEFAULTS);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    getOsdConfig(uid, CHANNEL_ID)
      .then((data) => {
        if (cancelled) return;
        setOsdDateTime({ ...OSD_DATETIME_DEFAULTS, ...(data?.osdDateTime || {}) });
        setOsdChannelName({ ...OSD_CHANNELNAME_DEFAULTS, ...(data?.osdChannelName || {}) });
      })
      .catch((error) => {
        if (cancelled) return;
        toast.error(getErrorMessage(error));
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, retryToken]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateOsdConfig(uid, { osdDateTime, osdChannelName });
      toast.success('Superposición OSD actualizada.');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const disabled = loading || saving;

  if (failed) {
    return (
      <CardShell icon={Type} title="OSD (superposición en pantalla)" testId="card-osd">
        <ErrorRetry onRetry={() => setRetryToken((token) => token + 1)} />
      </CardShell>
    );
  }

  return (
    <CardShell icon={Type} title="OSD (superposición en pantalla)" testId="card-osd">
      <div className="mb-5">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Fecha y hora</h4>
        <label htmlFor="osd-datetime-state" className="flex items-center gap-2 mb-2 text-sm text-gray-700">
          <input
            id="osd-datetime-state"
            type="checkbox"
            disabled={disabled}
            checked={osdDateTime.state}
            onChange={(event) => setOsdDateTime((prev) => ({ ...prev, state: event.target.checked }))}
            className="rounded border-gray-300"
          />
          Mostrar fecha y hora
        </label>
        <div className="grid grid-cols-2 gap-3">
          <EnumSelect
            id="osd-datetime-h"
            label="Posición horizontal"
            value={osdDateTime.horizontalPosition}
            options={OSD_HORIZONTAL_OPTIONS}
            disabled={disabled}
            onChange={(value) => setOsdDateTime((prev) => ({ ...prev, horizontalPosition: value }))}
          />
          <EnumSelect
            id="osd-datetime-v"
            label="Posición vertical"
            value={osdDateTime.verticalPosition}
            options={OSD_VERTICAL_OPTIONS}
            disabled={disabled}
            onChange={(value) => setOsdDateTime((prev) => ({ ...prev, verticalPosition: value }))}
          />
        </div>
      </div>

      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Nombre de cámara</h4>
        <label htmlFor="osd-channelname-state" className="flex items-center gap-2 mb-2 text-sm text-gray-700">
          <input
            id="osd-channelname-state"
            type="checkbox"
            disabled={disabled}
            checked={osdChannelName.state}
            onChange={(event) => setOsdChannelName((prev) => ({ ...prev, state: event.target.checked }))}
            className="rounded border-gray-300"
          />
          Activar superposición de nombre
        </label>
        <div className="mb-2">
          <label htmlFor="osd-channelname-name" className={labelClasses}>Nombre de cámara</label>
          <input
            id="osd-channelname-name"
            type="text"
            disabled={disabled}
            value={osdChannelName.name}
            onChange={(event) => setOsdChannelName((prev) => ({ ...prev, name: event.target.value }))}
            className={inputClasses}
          />
        </div>
        <div className="grid grid-cols-2 gap-3 mb-2">
          <EnumSelect
            id="osd-channelname-h"
            label="Posición horizontal"
            value={osdChannelName.horizontalPosition}
            options={OSD_HORIZONTAL_OPTIONS}
            disabled={disabled}
            onChange={(value) => setOsdChannelName((prev) => ({ ...prev, horizontalPosition: value }))}
          />
          <EnumSelect
            id="osd-channelname-v"
            label="Posición vertical"
            value={osdChannelName.verticalPosition}
            options={OSD_VERTICAL_OPTIONS}
            disabled={disabled}
            onChange={(value) => setOsdChannelName((prev) => ({ ...prev, verticalPosition: value }))}
          />
        </div>
        <label htmlFor="osd-channelname-watermark" className="flex items-center gap-2 text-sm text-gray-700">
          <input
            id="osd-channelname-watermark"
            type="checkbox"
            disabled={disabled}
            checked={osdChannelName.watermarkState}
            onChange={(event) => setOsdChannelName((prev) => ({ ...prev, watermarkState: event.target.checked }))}
            className="rounded border-gray-300"
          />
          Marca de agua
        </label>
      </div>

      <button type="button" onClick={handleSave} disabled={disabled} className={saveButtonClasses(disabled)}>
        {saving ? 'Guardando…' : 'Guardar'}
      </button>
    </CardShell>
  );
};

// ---- Sensor PIR ----

// PirConfigRequest.java has all three fields @NotNull (enabled, sensitivity
// 0-100, reduceMistakes), so unlike LedStateRequest below this isn't a
// partial-update shape - the form always sends the complete triple.
const PIR_DEFAULTS = { enabled: false, sensitivity: 0, reduceMistakes: false };

const PirConfigCard = ({ uid }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(PIR_DEFAULTS);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    getPirConfig(uid)
      .then((data) => {
        if (cancelled) return;
        setForm({
          enabled: Boolean(data?.enabled),
          sensitivity: Number(data?.sensitivity ?? 0),
          reduceMistakes: Boolean(data?.reduceMistakes),
        });
      })
      .catch((error) => {
        if (cancelled) return;
        toast.error(getErrorMessage(error));
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, retryToken]);

  const disabled = loading || saving;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePirConfig(uid, {
        enabled: form.enabled,
        sensitivity: form.sensitivity,
        reduceMistakes: form.reduceMistakes,
      });
      toast.success('Configuración del sensor PIR actualizada.');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (failed) {
    return (
      <CardShell icon={Radar} title="Sensor PIR" testId="card-pir-config">
        <ErrorRetry onRetry={() => setRetryToken((token) => token + 1)} />
      </CardShell>
    );
  }

  return (
    <CardShell icon={Radar} title="Sensor PIR" testId="card-pir-config">
      <label htmlFor="pir-enabled" className="flex items-center gap-2 mb-4 text-sm text-gray-700">
        <input
          id="pir-enabled"
          type="checkbox"
          disabled={disabled}
          checked={form.enabled}
          onChange={(event) => setForm((prev) => ({ ...prev, enabled: event.target.checked }))}
          className="rounded border-gray-300"
        />
        Activar sensor PIR
      </label>

      <div className="mb-4">
        <label htmlFor="pir-sensitivity" className={labelClasses}>
          Sensibilidad ({form.sensitivity})
        </label>
        <input
          id="pir-sensitivity"
          type="range"
          min={0}
          max={100}
          disabled={disabled}
          value={form.sensitivity}
          onChange={(event) => setForm((prev) => ({ ...prev, sensitivity: Number(event.target.value) }))}
          className="w-full"
        />
      </div>

      <label htmlFor="pir-reduceMistakes" className="flex items-center gap-2 mb-4 text-sm text-gray-700">
        <input
          id="pir-reduceMistakes"
          type="checkbox"
          disabled={disabled}
          checked={form.reduceMistakes}
          onChange={(event) => setForm((prev) => ({ ...prev, reduceMistakes: event.target.checked }))}
          className="rounded border-gray-300"
        />
        Reducir falsas detecciones
      </label>

      <button type="button" onClick={handleSave} disabled={disabled} className={saveButtonClasses(disabled)}>
        {saving ? 'Guardando…' : 'Guardar'}
      </button>
    </CardShell>
  );
};

// ---- Estado de LEDs ----

// LedStateRequest.java: every field is optional (partial update - "the caller
// sends only the subset it wants to change"), same shape as OsdRequest's
// osdDateTime/osdChannelName. Following the precedent already set by OsdCard
// and FtpCaptureCard above: the form is fully hydrated from GET and the
// complete managed shape is always sent back on save, rather than diffing
// which individual fields the user touched. No @Pattern/enum constraint was
// found on irLedState/powerLedState/doorbellLedState in the Java source, but
// the ROP API doc lists their allowed values, so they render as selects.
// doorbellAbilities is GET-only (no setter in
// LedStateRequest) so it's shown as read-only info text, like
// videoIntervalsOptions/extraFeatures in the FTP card.
const LED_STATE_DEFAULTS = { irLedState: '', powerLedState: '', doorbellLedState: '', irLedBrightness: 0 };

const LedStateCard = ({ uid }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(LED_STATE_DEFAULTS);
  const [doorbellAbilities, setDoorbellAbilities] = useState([]);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    getLedState(uid)
      .then((data) => {
        if (cancelled) return;
        setForm({
          irLedState: data?.irLedState ?? '',
          powerLedState: data?.powerLedState ?? '',
          doorbellLedState: data?.doorbellLedState ?? '',
          irLedBrightness: Number(data?.irLedBrightness ?? 0),
        });
        setDoorbellAbilities(data?.doorbellAbilities || []);
      })
      .catch((error) => {
        if (cancelled) return;
        toast.error(getErrorMessage(error));
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, retryToken]);

  const handleSelect = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const disabled = loading || saving;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateLedState(uid, {
        irLedState: form.irLedState,
        powerLedState: form.powerLedState,
        doorbellLedState: form.doorbellLedState,
        irLedBrightness: form.irLedBrightness,
      });
      toast.success('Estado de LEDs actualizado.');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (failed) {
    return (
      <CardShell icon={Lightbulb} title="Estado de LEDs" testId="card-led-state">
        <ErrorRetry onRetry={() => setRetryToken((token) => token + 1)} />
      </CardShell>
    );
  }

  return (
    <CardShell icon={Lightbulb} title="Estado de LEDs" testId="card-led-state">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <EnumSelect
          id="led-irLedState"
          label="LED IR"
          value={form.irLedState}
          options={LED_IR_STATE_OPTIONS}
          disabled={disabled}
          onChange={handleSelect('irLedState')}
        />
        <EnumSelect
          id="led-powerLedState"
          label="LED de encendido"
          value={form.powerLedState}
          options={LED_POWER_STATE_OPTIONS}
          disabled={disabled}
          onChange={handleSelect('powerLedState')}
        />
        <EnumSelect
          id="led-doorbellLedState"
          label="LED de timbre"
          value={form.doorbellLedState}
          options={LED_DOORBELL_STATE_OPTIONS}
          disabled={disabled}
          onChange={handleSelect('doorbellLedState')}
        />
      </div>

      <div className="mb-4">
        <label htmlFor="led-irLedBrightness" className={labelClasses}>
          Brillo del LED IR ({form.irLedBrightness})
        </label>
        <input
          id="led-irLedBrightness"
          type="range"
          min={0}
          max={100}
          disabled={disabled}
          value={form.irLedBrightness}
          onChange={(event) => setForm((prev) => ({ ...prev, irLedBrightness: Number(event.target.value) }))}
          className="w-full"
        />
      </div>

      {doorbellAbilities.length > 0 && (
        <p className="text-xs text-gray-500 mb-3">Funciones de timbre: {doorbellAbilities.join(', ')}</p>
      )}

      <button type="button" onClick={handleSave} disabled={disabled} className={saveButtonClasses(disabled)}>
        {saving ? 'Guardando…' : 'Guardar'}
      </button>
    </CardShell>
  );
};

// ---- Nombre del dispositivo ----

// GeneralConfigRequest.java: deviceName is @NotBlank @Size(min=1, max=31).
const DEVICE_NAME_MAX_LENGTH = 31;

const GeneralConfigCard = ({ uid }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    getGeneralConfig(uid)
      .then((data) => {
        if (cancelled) return;
        setDeviceName(data?.deviceName ?? '');
      })
      .catch((error) => {
        if (cancelled) return;
        toast.error(getErrorMessage(error));
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, retryToken]);

  const trimmedName = deviceName.trim();
  const isInvalid = trimmedName === '' || trimmedName.length > DEVICE_NAME_MAX_LENGTH;
  const disabled = loading || saving;

  const handleSave = async () => {
    if (isInvalid) return;
    setSaving(true);
    try {
      await updateGeneralConfig(uid, { deviceName });
      toast.success('Nombre del dispositivo actualizado.');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (failed) {
    return (
      <CardShell icon={Tag} title="Nombre del dispositivo" testId="card-general-config">
        <ErrorRetry onRetry={() => setRetryToken((token) => token + 1)} />
      </CardShell>
    );
  }

  return (
    <CardShell icon={Tag} title="Nombre del dispositivo" testId="card-general-config">
      <label htmlFor="general-config-deviceName" className={labelClasses}>
        Nombre del dispositivo
      </label>
      <input
        id="general-config-deviceName"
        type="text"
        maxLength={DEVICE_NAME_MAX_LENGTH}
        disabled={disabled}
        value={deviceName}
        onChange={(event) => setDeviceName(event.target.value)}
        className={`${inputClasses} mb-2`}
      />
      {isInvalid && !loading && (
        <p className="text-xs text-red-600 mb-2">
          Ingresá un nombre de entre 1 y {DEVICE_NAME_MAX_LENGTH} caracteres.
        </p>
      )}
      <button
        type="button"
        onClick={handleSave}
        disabled={disabled || isInvalid}
        className={saveButtonClasses(disabled || isInvalid)}
      >
        {saving ? 'Guardando…' : 'Guardar'}
      </button>
    </CardShell>
  );
};

// ---- Notificaciones de alarma ----

const AlarmConfigCard = ({ uid }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    getAlarmConfig(uid)
      .then((data) => {
        if (cancelled) return;
        setEnabled(Boolean(data?.enabled));
      })
      .catch((error) => {
        if (cancelled) return;
        toast.error(getErrorMessage(error));
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, retryToken]);

  const disabled = loading || saving;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAlarmConfig(uid, { enabled });
      toast.success('Notificaciones de alarma actualizadas.');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (failed) {
    return (
      <CardShell icon={Bell} title="Notificaciones de alarma" testId="card-alarm">
        <ErrorRetry onRetry={() => setRetryToken((token) => token + 1)} />
      </CardShell>
    );
  }

  return (
    <CardShell icon={Bell} title="Notificaciones de alarma" testId="card-alarm">
      <label htmlFor="alarm-enabled" className="flex items-center gap-2 mb-4 text-sm text-gray-700">
        <input
          id="alarm-enabled"
          type="checkbox"
          disabled={disabled}
          checked={enabled}
          onChange={(event) => setEnabled(event.target.checked)}
          className="rounded border-gray-300"
        />
        Activar notificaciones de alarma
      </label>

      <button type="button" onClick={handleSave} disabled={disabled} className={saveButtonClasses(disabled)}>
        {saving ? 'Guardando…' : 'Guardar'}
      </button>
    </CardShell>
  );
};

// ---- Batería (solo lectura) ----

// BatteryStatusResponse is read-only telemetry (no PUT counterpart). Some ROP
// camera models are mains-powered and don't report battery data, so a failed
// GET (e.g. RopApiException mapped to an error status) is shown as a graceful
// inline message instead of a toast, rather than looking like a broken card.
const BatteryCard = ({ uid }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [battery, setBattery] = useState({ percent: null, chargeStatus: '' });
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    getBatteryStatus(uid)
      .then((data) => {
        if (cancelled) return;
        setBattery({ percent: data?.percent ?? null, chargeStatus: data?.chargeStatus ?? '' });
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, retryToken]);

  const percent = Math.max(0, Math.min(100, battery.percent ?? 0));

  return (
    <CardShell icon={Battery} title="Batería" testId="card-battery">
      {loading ? (
        <p className="text-sm text-gray-500">Cargando…</p>
      ) : failed ? (
        <ErrorRetry
          message="No se pudo obtener el estado de batería."
          onRetry={() => setRetryToken((token) => token + 1)}
        />
      ) : (
        <div>
          <p className="text-sm text-gray-700 mb-1">
            Carga: <span className="font-semibold">{battery.percent ?? '—'}%</span>
          </p>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
            <div className="h-full bg-blue-600" style={{ width: `${percent}%` }} />
          </div>
          <p className="text-sm text-gray-700">Estado: {battery.chargeStatus || '—'}</p>
        </div>
      )}
    </CardShell>
  );
};

// ---- SIM / Red móvil (solo lectura) ----

// Only 4G cameras have a SIM, so the GET can fail simply because this model
// doesn't have one — but it can also fail for an unrelated reason (outage,
// auth). Either way it's shown as a graceful inline message instead of a
// toast, and the message itself says which one it was (see getErrorMessage).
const MobileNetworkCard = ({ uid }) => {
  const [loading, setLoading] = useState(true);
  // Holds the reason the fetch failed, so the card can say what actually went
  // wrong (e.g. this camera has no SIM module) instead of always presenting
  // any failure — outages and auth errors included — as a hardware limitation.
  const [error, setError] = useState(null);
  const [info, setInfo] = useState({ iccid: '', imei: '', phoneNumber: '' });
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMobileNetwork(uid)
      .then((data) => {
        if (cancelled) return;
        setInfo({
          iccid: data?.iccid ?? '',
          imei: data?.imei ?? '',
          phoneNumber: data?.phoneNumber ?? '',
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(getErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, retryToken]);

  return (
    <CardShell icon={Smartphone} title="SIM / Red móvil" testId="card-mobile-network">
      {loading ? (
        <p className="text-sm text-gray-500">Cargando…</p>
      ) : error ? (
        <ErrorRetry message={error} onRetry={() => setRetryToken((token) => token + 1)} />
      ) : (
        <div className="space-y-1 text-sm text-gray-700">
          <p>ICCID: <span className="font-semibold">{info.iccid || '—'}</span></p>
          <p>IMEI: <span className="font-semibold">{info.imei || '—'}</span></p>
          <p>Número de teléfono: <span className="font-semibold">{info.phoneNumber || '—'}</span></p>
        </div>
      )}
    </CardShell>
  );
};

// ---- Tarjeta SD ----

const BYTES_PER_MB = 1024 ** 2;
const BYTES_PER_GB = 1024 ** 3;

// SdCardResponse capacities are in BYTES.
const formatBytes = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value < 0) return '—';
  if (value >= BYTES_PER_GB) return `${(value / BYTES_PER_GB).toFixed(1)} GB`;
  return `${(value / BYTES_PER_MB).toFixed(1)} MB`;
};

const SD_CARD_DEFAULTS = {
  sdNumber: 0,
  formatStatus: '',
  mountStatus: '',
  totalCapacity: 0,
  availableCapacity: 0,
};

const SdCardCard = ({ uid, isAdmin }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [sd, setSd] = useState(SD_CARD_DEFAULTS);
  const [confirming, setConfirming] = useState(false);
  const [formatting, setFormatting] = useState(false);
  // Bumped to re-run the fetch effect below without unmounting the card, so a
  // failed load (including a failed post-format refetch) can be retried from
  // right there instead of forcing the user to leave and re-enter the tab.
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    getSdCard(uid)
      .then((data) => {
        if (cancelled) return;
        setSd({ ...SD_CARD_DEFAULTS, ...data });
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, retryToken]);

  const total = Number(sd.totalCapacity) || 0;
  const free = Math.min(Number(sd.availableCapacity) || 0, total);
  const used = Math.max(0, total - free);
  const usedPercent = total > 0 ? Math.round((used / total) * 100) : 0;

  const handleFormat = async () => {
    setFormatting(true);
    try {
      await formatSdCard(uid, { sdNumber: sd.sdNumber });
    } catch (error) {
      setFormatting(false);
      toast.error(getErrorMessage(error));
      return;
    }

    // The format itself succeeded, so from here on a failure only means we
    // couldn't refresh the card — it must never be reported (or read) as a
    // failed format, and the pre-format numbers must not linger looking current.
    toast.success('Tarjeta SD formateada.');
    setConfirming(false);
    try {
      const data = await getSdCard(uid);
      setSd({ ...SD_CARD_DEFAULTS, ...data });
    } catch {
      setFailed(true);
    } finally {
      setFormatting(false);
    }
  };

  return (
    <CardShell icon={HardDrive} title="Tarjeta SD" testId="card-sd-card">
      {loading ? (
        <p className="text-sm text-gray-500">Cargando…</p>
      ) : failed ? (
        <ErrorRetry
          message="No se pudo obtener la información de la tarjeta SD."
          onRetry={() => setRetryToken((token) => token + 1)}
        />
      ) : (
        <div>
          <div className="space-y-1 text-sm text-gray-700 mb-3">
            <p>Estado: <span className="font-semibold">{sd.formatStatus || '—'}</span></p>
            <p>Montaje: <span className="font-semibold">{sd.mountStatus || '—'}</span></p>
            <p>Capacidad total: <span className="font-semibold">{formatBytes(total)}</span></p>
            <p>Usado: <span className="font-semibold">{formatBytes(used)}</span> ({usedPercent}%)</p>
            <p>Libre: <span className="font-semibold">{formatBytes(free)}</span></p>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-4">
            <div className="h-full bg-blue-600" style={{ width: `${usedPercent}%` }} />
          </div>

          {/* El formateo borra todos los datos, así que queda restringido a
              admin; el resto de la info de la SD (capacidad, estado, etc.)
              se muestra a todos los usuarios más arriba. */}
          {isAdmin &&
            (confirming ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-sm text-red-700 mb-3">
                  Se borrarán todos los datos de la tarjeta SD. Esta acción no se puede deshacer.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleFormat}
                    disabled={formatting}
                    className={`rounded-lg bg-red-600 text-white py-2 px-4 text-sm font-medium hover:bg-red-700 transition-colors ${
                      formatting ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                  >
                    {formatting ? 'Formateando…' : 'Confirmar formateo'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    disabled={formatting}
                    className="rounded-lg border border-gray-200 text-gray-700 py-2 px-4 text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-60"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="rounded-lg border border-red-200 text-red-600 py-2 px-4 text-sm font-medium hover:bg-red-50 transition-colors"
              >
                Formatear tarjeta SD
              </button>
            ))}
        </div>
      )}
    </CardShell>
  );
};

// ---- Tab root ----

// Non-admin users get: timezone, FTP — Horario (calendario), device name,
// battery, read-only SIM info y la tarjeta SD (sin el botón de formatear).
// Horario de grabación, FTP — Configuración, OSD, PIR, LEDs y Alarma son
// admin-only; no se renderizan en absoluto, así que su GET nunca se dispara.
const RopConfiguracionTab = ({ uid }) => {
  const isAdmin = useIsAdmin();

  if (!uid) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <p className="text-sm text-gray-600 text-center">
          Esta cámara no tiene un dispositivo ROP vinculado.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TimezoneCard uid={uid} />
      {isAdmin && (
        <ScheduleCard
          uid={uid}
          testId="card-recording-schedule"
          icon={CalendarClock}
          title="Horario de grabación"
          getFn={getRecordingSchedule}
          updateFn={updateRecordingSchedule}
          successMessage="Horario de grabación actualizado."
        />
      )}
      {isAdmin && <FtpCaptureCard uid={uid} />}
      <ScheduleCard
        uid={uid}
        testId="card-ftp-schedule"
        icon={CalendarCheck2}
        title="FTP — Horario"
        getFn={getFtpSchedule}
        updateFn={updateFtpSchedule}
        successMessage="Horario de FTP actualizado."
      />
      {isAdmin && <OsdCard uid={uid} />}
      {isAdmin && <PirConfigCard uid={uid} />}
      {isAdmin && <LedStateCard uid={uid} />}
      <GeneralConfigCard uid={uid} />
      {isAdmin && <AlarmConfigCard uid={uid} />}
      <BatteryCard uid={uid} />
      <MobileNetworkCard uid={uid} />
      <SdCardCard uid={uid} isAdmin={isAdmin} />
    </div>
  );
};

export default RopConfiguracionTab;
